import { useEffect, useMemo, useState } from 'react';
import type { LinkButton } from '@/data/store';
import { loadQuickLinks, type QuickLink } from '@/lib/quickLinks';
import { SheetOption } from './Overlay';
import { PinIcon, StarIcon, TagIcon } from './Icons';
// On a phone the keyboard stays down until they tap the search: the list is the point (16 Oct).
import { isPhone } from '@/lib/device';

// + menu, Quick links: search, this customer's operator first, then operator pages, Google
// reviews and our pages. Picking one only fills the message box (Gary, 15 Oct).
// Each heading opens and closes; only this customer's operator starts open, and a search
// opens everything it finds (Gary, 16 Oct).

const THEIRS = "This customer's operator";

const ICONS: Record<string, JSX.Element> = { 'Operator pages': <PinIcon />, 'Google reviews': <StarIcon />, 'Our pages': <TagIcon /> };

export function QuickLinksSheet({ brand, territoryId, onUse }: { brand: 'cmb' | 'pos'; territoryId?: string; onUse: (message: string, link: LinkButton) => void }) {
  const [links, setLinks] = useState<QuickLink[] | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set([THEIRS]));
  const searching = search.trim() !== '';
  useEffect(() => {
    let gone = false;
    void loadQuickLinks(brand).then((l) => { if (!gone) setLinks(l); }).catch(() => { if (!gone) setLinks([]); });
    return () => { gone = true; };
  }, [brand]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const shown = (links ?? []).filter((l) => !term || `${l.title} ${l.sub} ${l.group}`.toLowerCase().includes(term));
    const out: { label: string; items: QuickLink[] }[] = [];
    const theirs = term ? [] : shown.filter((l) => territoryId && l.territoryId === territoryId);
    if (theirs.length) out.push({ label: THEIRS, items: theirs });
    for (const label of ['Operator pages', 'Google reviews', 'Our pages']) {
      const items = shown.filter((l) => l.group === label);
      if (items.length) out.push({ label, items });
    }
    return out;
  }, [links, search, territoryId]);

  return (
    <>
      <h2 className="chats-sheet-title">Quick links</h2>
      <p className="chats-sheet-hint">Puts a ready message in your message box, with the page as a button under it, to check and send.</p>
      <input className="chats-input chats-quick-search" type="search" autoFocus={!isPhone} placeholder="Search, e.g. Hermanus or pricing" aria-label="Search quick links" value={search} onChange={(e) => setSearch(e.target.value)} />
      {links === null && <p className="chats-sheet-hint">Loading...</p>}
      {links !== null && groups.length === 0 && <p className="chats-sheet-hint">{search ? 'Nothing matches.' : 'No quick links yet. Head office adds them in Sentinel.'}</p>}
      {groups.map((g) => (
        <details
          key={g.label}
          className="chats-quick-section"
          open={searching || open.has(g.label)}
          onToggle={(e) => {
            if (searching) return;
            const isOpen = e.currentTarget.open;
            setOpen((prev) => {
              if (prev.has(g.label) === isOpen) return prev;
              const next = new Set(prev);
              if (isOpen) next.add(g.label); else next.delete(g.label);
              return next;
            });
          }}
        >
          <summary className="chats-group-title chats-quick-group">{g.label}</summary>
          {g.items.map((l) => (
            <SheetOption key={`${g.label}-${l.id}`} icon={ICONS[l.group] ?? <TagIcon />} title={l.title} sub={l.sub} onClick={() => onUse(l.message, l.link)} />
          ))}
        </details>
      ))}
    </>
  );
}
